/**
 * Sentry boot stub — tiny (<1KB gzipped).
 *
 * Ships in the main chunk. Installs window.onerror + unhandledrejection
 * listeners synchronously so we don't lose boot-time errors. Queues events.
 * Schedules dynamic import of the full Sentry SDK (424KB gzipped) via
 * requestIdleCallback so it's off the first-paint critical path.
 *
 * Once the full SDK loads, it drains the queue via `window.__sentryBootQueue`.
 *
 * GAP-75: prior state had `@/lib/sentry` imported statically from main.tsx,
 * pulling the 424KB Sentry vendor chunk into first-paint blocking. At p50
 * on 3G this added ~100ms before React mounted.
 */

export interface QueuedError {
  kind: "error" | "unhandled_rejection" | "console_log";
  timestamp: number;
  error?: Error;
  message?: string;
  source?: string;
  lineno?: number;
  colno?: number;
  reason?: unknown;
  /** Console method when kind === "console_log" (error / warn). */
  consoleLevel?: "error" | "warn";
  /** Raw console arguments when kind === "console_log". */
  consoleArgs?: unknown[];
}

declare global {
  interface Window {
    __sentryBootQueue?: QueuedError[];
  }
}

const queue: QueuedError[] = [];
if (typeof window !== "undefined") {
  window.__sentryBootQueue = queue;

  window.addEventListener("error", (e) => {
    queue.push({
      kind: "error",
      timestamp: Date.now(),
      error: e.error instanceof Error ? e.error : undefined,
      message: e.message,
      source: e.filename,
      lineno: e.lineno,
      colno: e.colno,
    });
  });

  window.addEventListener("unhandledrejection", (e) => {
    queue.push({
      kind: "unhandled_rejection",
      timestamp: Date.now(),
      reason: e.reason,
    });
  });

  // G7: also intercept console.error / console.warn at boot. Capture.ts
  // (S3) installs its own console patch later as the first action in
  // main.tsx — that patch wraps OUR wrapper here (D18 order). For the
  // window between page load and capture.ts loading, this boot patch
  // ensures NO console error or warning is lost. The patch is tier-aware:
  // skip on production tier (no overlay, no diagnostic value).
  // VITE_DEPLOY_ENV is replaced as a literal by Vite at build time so
  // the entire if-block is DCE'd on prod.
  if (import.meta.env.VITE_DEPLOY_ENV !== "production") {
    const origError = console.error.bind(console);
    const origWarn = console.warn.bind(console);
    console.error = function (...args: unknown[]) {
      queue.push({
        kind: "console_log",
        timestamp: Date.now(),
        consoleLevel: "error",
        consoleArgs: args,
        message: args[0] instanceof Error ? args[0].message : String(args[0] ?? ""),
        error: args[0] instanceof Error ? args[0] : undefined,
      });
      origError(...args);
    };
    console.warn = function (...args: unknown[]) {
      queue.push({
        kind: "console_log",
        timestamp: Date.now(),
        consoleLevel: "warn",
        consoleArgs: args,
        message: String(args[0] ?? ""),
      });
      origWarn(...args);
    };
  }
}

/**
 * Schedule the full Sentry SDK to load off the critical path.
 * Uses requestIdleCallback where available, setTimeout fallback.
 */
export function deferSentryInit(): void {
  if (typeof window === "undefined") return;

  const load = () => {
    import("@/lib/sentry")
      .then((mod) => {
        if (typeof mod.drainBootQueue === "function") {
          mod.drainBootQueue(queue);
        }
        // Queue is drained; future errors go through Sentry's own handlers.
        delete window.__sentryBootQueue;
      })
      .catch(() => {
        // If Sentry fails to load, we just lose visibility. Don't crash.
      });
  };

  if ("requestIdleCallback" in window) {
    (window as unknown as {
      requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void;
    }).requestIdleCallback(load, { timeout: 3000 });
  } else {
    setTimeout(load, 1500);
  }
}
